import type { DigitShape } from "@positive/format";
import { isLanguage, type Language } from "@positive/i18n";
import { isThemeKey, type ThemeKey } from "@positive/ui";

/**
 * UI preferences of this browser (never tokens). Language, digit shape and Hijri display belong to the user's account
 * (`PUT /api/platform/me/preferences`, decision I6): after sign-in the account's values win and every change is stored
 * there. The copy kept here is only the choice before sign-in and an instant cache, so the next start shows the last
 * language without a flash. The theme stays per browser.
 */
export interface Preferences {
  language: Language;
  theme: ThemeKey;
  /** Digit shape chosen by the user; `null` = tenant setting. */
  digits: DigitShape | null;
  /** Hijri date shown beside Gregorian dates (ADR-0009/D-5, display only); `null` = tenant setting `platform.locale`. */
  hijri: boolean | null;
}

/** The part of the preferences stored on the user's account. */
export type AccountPreferenceValues = Pick<Preferences, "language" | "digits" | "hijri">;

const storageKey = "positive.preferences";

function detectLanguage(): Language {
  return navigator.language.toLowerCase().startsWith("ar") ? "ar" : "en";
}

function detectTheme(): ThemeKey {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "evening" : "morning";
}

function isDigitShape(value: unknown): value is DigitShape {
  return value === "latn" || value === "arab";
}

export function loadPreferences(): Preferences {
  const defaults: Preferences = {
    language: detectLanguage(),
    theme: detectTheme(),
    digits: null,
    hijri: null,
  };
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) ?? "{}") as Partial<Record<keyof Preferences, unknown>>;
    return {
      language: isLanguage(stored.language) ? stored.language : defaults.language,
      theme: isThemeKey(stored.theme) ? stored.theme : defaults.theme,
      digits: isDigitShape(stored.digits) ? stored.digits : null,
      hijri: typeof stored.hijri === "boolean" ? stored.hijri : null,
    };
  } catch {
    return defaults;
  }
}

export function savePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch {
    // Storage can be unavailable (private mode); preferences then last for the session only.
  }
}
